import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { firstValueFrom, take } from 'rxjs';
import { RmsModule } from '@rms';
import { JournalAction, LedgerService, PromptService, RmsMessageService, lastValueFrom } from '@services';
import { RMS_PULLDOWN_DATA } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';
import { RmsPulldownRef } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';

interface JournalLine {
  AccountID: number | null;
  Description: string;
  DebitAmount: number | null;
  CreditAmount: number | null;
  LedgerTransactionID?: number | null;
  IsCashAccount?: boolean;
}

const JOURNAL_METHOD_IDS = [8, 5, 3, 4, 2, 7, 11];
const CHECK_METHOD_ID = 8;

@Component({
  selector: 'app-journal-form',
  imports: [RmsModule],
  templateUrl: './journal-form.component.html',
  styleUrl: './journal-form.component.scss'
})
export class JournalFormComponent implements OnInit {
  JournalEntryID: number | null = null;
  Entry: any = null;
  History: any[] = [];
  AccountOptions: { value: number; label: string }[] = [];
  CashAccountIDs = new Set<number>();
  PaymentMethodOptions: { value: any; label: string }[] = [];
  Periods: { StartDate: string; EndDate: string; PeriodName: string; Status: string }[] = [];
  CanPrepare = false;
  CanApprove = false;
  UserGuid = '';
  AllowSelfApproval = false;
  Loading = true;
  Busy = false;

  Form = {
    EntryDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substring(0, 10),
    Reason: '',
    SourceReference: '',
    Description: '',
    SourceDocumentID: '',
    ExternalReference: '',
    PaymentMethodID: '' as any,
    CheckNumber: ''
  };
  Lines: JournalLine[] = [this.blankLine(), this.blankLine()];

  constructor(
    @Inject(RMS_PULLDOWN_DATA) private data: { JournalEntryID?: number } | null,
    private ref: RmsPulldownRef,
    private ledger: LedgerService,
    private prompt: PromptService,
    private message: RmsMessageService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    this.JournalEntryID = this.data?.JournalEntryID ?? null;
    try {
      const [options, me, periods] = await Promise.all([
        lastValueFrom(this.ledger.getFilterOptions()),
        lastValueFrom(this.ledger.getMyRoles()),
        lastValueFrom(this.ledger.getPeriods())
      ]);
      this.CanPrepare = !!me?.roles?.preparer;
      this.CanApprove = !!me?.roles?.approver;
      this.UserGuid = String(me?.userGuid || '').toLowerCase();
      this.AllowSelfApproval = !!me?.allowSelfApproval;
      this.Periods = (periods || []).map((p: any) => ({
        StartDate: String(p.StartDate).substring(0, 10),
        EndDate: String(p.EndDate).substring(0, 10),
        PeriodName: p.PeriodName,
        Status: p.Status
      }));
      this.AccountOptions = (options?.accounts || []).map((a: any) => ({ value: a.AccountID, label: `${a.AccountNumber} ${a.AccountName}` }));
      this.CashAccountIDs = new Set((options?.accounts || []).filter((a: any) => a.IsCashAccount).map((a: any) => a.AccountID));
      // 7.1.26: the same real payment methods serve money in and money out
      this.PaymentMethodOptions = [{ value: '', label: 'None' }, ...(options?.paymentMethods || [])
        .filter((m: any) => JOURNAL_METHOD_IDS.includes(m.PaymentMethodID))
        .map((m: any) => ({ value: m.PaymentMethodID, label: m.MethodName }))];
      if (this.JournalEntryID) await this.load();
    } catch (err) {
      this.message.error('Failed to load journal: ' + this.errorText(err));
    } finally {
      this.Loading = false;
      this.cdr.detectChanges();
    }
  }

  private async load() {
    const detail = await lastValueFrom(this.ledger.getJournal(this.JournalEntryID!));
    this.Entry = detail?.entry || null;
    this.History = detail?.history || [];
    if (!this.Entry) return;
    this.Form = {
      EntryDate: String(this.Entry.EntryDate).substring(0, 10),
      Reason: this.Entry.Reason || '',
      SourceReference: this.Entry.SourceReference || '',
      Description: this.Entry.Description || '',
      SourceDocumentID: this.Entry.SourceDocumentID || '',
      ExternalReference: this.Entry.ExternalReference || '',
      PaymentMethodID: this.Entry.PaymentMethodID ?? '',
      CheckNumber: this.Entry.CheckNumber || ''
    };
    this.Lines = (detail?.lines || []).map((l: any) => ({
      AccountID: l.AccountID,
      Description: l.Description || '',
      DebitAmount: l.DebitAmount ? Number(l.DebitAmount) : null,
      CreditAmount: l.CreditAmount ? Number(l.CreditAmount) : null,
      LedgerTransactionID: l.LedgerTransactionID ?? null,
      IsCashAccount: !!l.IsCashAccount
    }));
    if (this.Editable && this.Lines.length < 2) this.Lines.push(...Array.from({ length: 2 - this.Lines.length }, () => this.blankLine()));
  }

  get IsNew(): boolean { return !this.JournalEntryID; }
  get Status(): string { return this.IsNew ? 'New' : (this.Entry?.Status || ''); }
  get IsDeleted(): boolean { return !!this.Entry?.IsDeleted; }
  get Editable(): boolean { return this.CanPrepare && (this.IsNew || (this.Status === 'Draft' && !this.IsDeleted)); }

  // drafts can be saved into a closed month; only submit is blocked (7.1.12)
  get DatePeriod() {
    const date = this.Form.EntryDate;
    return this.Periods.find(p => date >= p.StartDate && date <= p.EndDate) || null;
  }
  get PeriodClosed(): boolean { return this.DatePeriod?.Status === 'Closed'; }

  get MethodLabel(): string {
    const cashLines = this.Lines.filter(l => l.AccountID && this.CashAccountIDs.has(l.AccountID));
    if (cashLines.some(l => Number(l.CreditAmount))) return 'Paid out by';
    if (cashLines.some(l => Number(l.DebitAmount))) return 'Received by';
    return 'Payment method';
  }
  get IsCheck(): boolean { return Number(this.Form.PaymentMethodID) === CHECK_METHOD_ID; }
  get MethodName(): string {
    return this.PaymentMethodOptions.find(m => String(m.value) === String(this.Form.PaymentMethodID))?.label || '';
  }

  get IsOwnJournal(): boolean {
    return !!this.Entry?.PreparedByGUID && String(this.Entry.PreparedByGUID).toLowerCase() === this.UserGuid;
  }
  // the server re-checks all of this; these only decide what the button says
  get PostBlockedReason(): string {
    if (!this.CanApprove) return 'You don\'t have permission to approve and post journals.';
    if (this.IsOwnJournal && !this.AllowSelfApproval) return 'You prepared this journal, so someone else must approve and post it.';
    if (this.PeriodClosed) return `Period ${this.DatePeriod?.PeriodName} is closed.`;
    return '';
  }
  get CanReverse(): boolean {
    return this.CanPrepare && this.Status === 'Posted' && !this.Entry?.ReversedByJournalEntryID && !this.Entry?.ReversesJournalEntryID;
  }

  get DebitTotal(): number { return this.round(this.Lines.reduce((s, l) => s + (Number(l.DebitAmount) || 0), 0)); }
  get CreditTotal(): number { return this.round(this.Lines.reduce((s, l) => s + (Number(l.CreditAmount) || 0), 0)); }
  get Difference(): number { return this.round(this.DebitTotal - this.CreditTotal); }
  get FilledLines(): JournalLine[] {
    return this.Lines.filter(l => l.AccountID || Number(l.DebitAmount) || Number(l.CreditAmount) || l.Description.trim());
  }
  get ReadyToSubmit(): boolean { return this.FilledLines.length >= 2 && this.Difference === 0 && this.DebitTotal > 0 && !this.PeriodClosed; }

  addLine() { this.Lines.push(this.blankLine()); }
  removeLine(index: number) { this.Lines.splice(index, 1); }

  // a line is one-sided (7.1.9): typing a debit clears the credit and vice versa
  amountChanged(line: JournalLine, side: 'debit' | 'credit') {
    if (side === 'debit' && Number(line.DebitAmount)) line.CreditAmount = null;
    if (side === 'credit' && Number(line.CreditAmount)) line.DebitAmount = null;
  }

  accountLabel(accountId: number | null): string {
    return this.AccountOptions.find(a => a.value === accountId)?.label || '—';
  }

  async saveDraft(): Promise<boolean> {
    if (!this.Form.EntryDate || !this.Form.Reason.trim() || !this.Form.SourceReference.trim()) {
      this.message.warn('Posting date, reason and source reference are required.');
      return false;
    }
    if (this.FilledLines.some(l => !l.AccountID)) {
      this.message.warn('Every line needs an account.');
      return false;
    }
    this.Busy = true;
    try {
      const saved = await lastValueFrom(this.ledger.saveJournal({
        JournalEntryID: this.JournalEntryID,
        RowVer: this.Entry?.RowVer ?? null,
        ...this.Form,
        PaymentMethodID: this.Form.PaymentMethodID || null,
        CheckNumber: this.IsCheck ? this.Form.CheckNumber : null,
        Lines: this.FilledLines.map(l => ({
          AccountID: l.AccountID,
          Description: l.Description,
          DebitAmount: Number(l.DebitAmount) || 0,
          CreditAmount: Number(l.CreditAmount) || 0
        }))
      }));
      this.JournalEntryID = saved.JournalEntryID;
      await this.load();
      return true;
    } catch (err) {
      this.message.error(this.errorText(err));
      return false;
    } finally {
      this.Busy = false;
      this.cdr.detectChanges();
    }
  }

  async save() {
    if (await this.saveDraft()) this.message.success(`${this.Entry?.JournalNumber} saved as draft.`);
  }

  async submit() {
    if (this.PeriodClosed) {
      this.message.warn(`Period ${this.DatePeriod?.PeriodName} is closed. Save the draft and submit it after the period is reopened.`);
      return;
    }
    if (!this.ReadyToSubmit) {
      this.message.warn('A journal needs at least two lines and debits must equal credits before it can be submitted.');
      return;
    }
    if (!(await this.saveDraft())) return;
    await this.runAction('submit', 'submitted for review');
  }

  async returnToDraft() {
    const reason = await this.askReason('Return this journal to the preparer?', 'Reason for returning');
    if (reason) await this.runAction('return', 'returned to draft', reason);
  }

  async cancelJournal() {
    const reason = await this.askReason('Cancel this journal? A canceled journal has no ledger impact and cannot be reopened.', 'Reason for canceling');
    if (reason) await this.runAction('cancel', 'canceled', reason);
  }

  async deleteJournal() {
    const reason = await this.askReason('Move this draft to the recycle bin? It can be restored later.', 'Reason for deleting');
    if (reason) await this.runAction('delete', 'moved to the recycle bin', reason);
  }

  async restore() {
    await this.runAction('restore', 'restored to draft');
  }

  async approveAndPost() {
    if (!this.JournalEntryID || !this.Entry || this.PostBlockedReason) return;
    const { closed$ } = this.prompt.open({
      promptText: `Approve and post ${this.Entry.JournalNumber}? It will be written to the ledger and can then only be corrected by a reversal.`,
      buttons: [{ label: 'Approve & post', value: true, buttonClass: 'btn-bg-green' }]
    });
    // a prompt without an input closes with the button value itself, not { buttonValue }
    const result: any = await firstValueFrom(closed$.pipe(take(1)));
    if (result !== true) return;
    this.Busy = true;
    this.cdr.detectChanges();
    try {
      const posted = await lastValueFrom(this.ledger.postJournal(this.JournalEntryID, this.Entry.RowVer));
      await this.load();
      this.message.success(`${posted.JournalNumber} posted: ${posted.PostedRows} ledger row(s) written.`);
    } catch (err) {
      this.message.error(this.errorText(err));
      await this.load().catch(() => undefined);
    } finally {
      this.Busy = false;
      this.cdr.detectChanges();
    }
  }

  // 7.1.14: correct a posted journal with a linked reversal, optionally plus a replacement draft
  async reverse(createReplacement: boolean) {
    if (!this.JournalEntryID || !this.Entry) return;
    const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substring(0, 10);
    const todayPeriod = this.Periods.find(p => today >= p.StartDate && today <= p.EndDate);
    const differentPeriod = todayPeriod?.PeriodName && todayPeriod.PeriodName !== this.Entry.PeriodName;
    const alert = differentPeriod
      ? ` Note: the reversal will be dated today (period ${todayPeriod!.PeriodName}), not in the original period ${this.Entry.PeriodName}.`
      : '';
    const { closed$ } = this.prompt.open({
      promptText: `Create a reversal of ${this.Entry.JournalNumber}${createReplacement ? ' and a replacement draft' : ''}? `
        + `The reversal is a new draft with debits and credits swapped; it goes through review and posting like any journal. The original stays posted.${alert}`,
      promptInput: 'textarea',
      inputLabel: 'Reason for the correction',
      inputRequired: true,
      buttons: [{ label: createReplacement ? 'Reverse & replace' : 'Reverse', value: true, buttonClass: 'btn-bg-yellow' }]
    });
    const result: any = await firstValueFrom(closed$.pipe(take(1)));
    if (!result?.buttonValue) return;
    const reason = String(result.inputValue || '').trim();
    if (!reason) { this.message.warn('A reason is required.'); return; }

    this.Busy = true;
    try {
      const created = await lastValueFrom(this.ledger.reverseJournal(this.JournalEntryID, this.Entry.RowVer, reason, today, createReplacement));
      await this.load();
      this.message.success(created.ReplacementJournalNumber
        ? `Reversal ${created.ReversalJournalNumber} and replacement ${created.ReplacementJournalNumber} created as drafts.`
        : `Reversal ${created.ReversalJournalNumber} created as a draft.`);
    } catch (err) {
      this.message.error(this.errorText(err));
      await this.load().catch(() => undefined);
    } finally {
      this.Busy = false;
      this.cdr.detectChanges();
    }
  }

  async openJournal(journalEntryId: number | null | undefined) {
    if (!journalEntryId) return;
    this.Loading = true;
    this.JournalEntryID = journalEntryId;
    try {
      await this.load();
    } catch (err) {
      this.message.error('Failed to load journal: ' + this.errorText(err));
    } finally {
      this.Loading = false;
      this.cdr.detectChanges();
    }
  }

  private async runAction(action: JournalAction, doneText: string, reason?: string) {
    if (!this.JournalEntryID || !this.Entry) return;
    this.Busy = true;
    try {
      await lastValueFrom(this.ledger.journalAction(this.JournalEntryID, action, this.Entry.RowVer, reason));
      await this.load();
      this.message.success(`${this.Entry?.JournalNumber} ${doneText}.`);
    } catch (err) {
      this.message.error(this.errorText(err));
      await this.load().catch(() => undefined);
    } finally {
      this.Busy = false;
      this.cdr.detectChanges();
    }
  }

  private async askReason(promptText: string, inputLabel: string): Promise<string | null> {
    const { closed$ } = this.prompt.open({
      promptText,
      promptInput: 'textarea',
      inputLabel,
      inputRequired: true,
      buttons: [{ label: 'Continue', value: true, buttonClass: 'btn-bg-blue' }]
    });
    const result: any = await firstValueFrom(closed$.pipe(take(1)));
    const reason = result?.buttonValue ? String(result.inputValue || '').trim() : '';
    if (result?.buttonValue && !reason) this.message.warn('A reason is required.');
    return reason || null;
  }

  close() { this.ref.close(true); }

  private blankLine(): JournalLine {
    return { AccountID: null, Description: '', DebitAmount: null, CreditAmount: null };
  }

  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private errorText(err: any): string {
    return err?.error?.error || err?.message || 'Unknown error';
  }
}
